/**
 * AppSelect — a styled replacement for native <select> that works in dark mode.
 *
 * Usage:
 *   <AppSelect
 *     value={strategyId}
 *     onChange={setStrategyId}
 *     placeholder="— No strategy —"
 *     options={strategies.map(s => ({ value: s.id, label: s.name }))}
 *   />
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface AppSelectOption {
  value: string;
  label: string;
}

interface AppSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: AppSelectOption[];
  placeholder?: string;
  className?: string;
  accentColor?: 'primary' | 'violet' | 'indigo';
}

const RING_CLASSES: Record<string, string> = {
  primary: 'focus-within:ring-primary-500',
  violet: 'focus-within:ring-violet-500',
  indigo: 'focus-within:ring-indigo-500',
};

const CHECK_CLASSES: Record<string, string> = {
  primary: 'text-primary-500',
  violet: 'text-[var(--text-ter)]',
  indigo: 'text-[var(--text-ter)]',
};

export function AppSelect({
  value,
  onChange,
  options,
  placeholder = '— None —',
  className = '',
  accentColor = 'primary',
}: AppSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? placeholder;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  function handleSelect(v: string) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm  border
          border-[var(--rule)]
          bg-[var(--bg-raised)]
          text-[var(--text-pri)]
          hover:border-[var(--rule)] dark:hover:border-[var(--rule)]
          focus:outline-none focus:ring-2 focus:ring-offset-0
          ${RING_CLASSES[accentColor]}
          transition-colors`}
      >
        <span className={`truncate ${!selected ? 'text-[var(--text-ter)]' : ''}`}>
          {displayLabel}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg overflow-hidden">
          <ul className="max-h-52 overflow-y-auto py-1">
            {/* Blank / placeholder option */}
            <li>
              <button
                type="button"
                onClick={() => handleSelect('')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors"
              >
                <span className="flex-1 truncate">{placeholder}</span>
                {value === '' && <Check className={`h-3.5 w-3.5 flex-shrink-0 ${CHECK_CLASSES[accentColor]}`} />}
              </button>
            </li>

            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors"
                >
                  <span className="flex-1 truncate">{opt.label}</span>
                  {value === opt.value && (
                    <Check className={`h-3.5 w-3.5 flex-shrink-0 ${CHECK_CLASSES[accentColor]}`} />
                  )}
                </button>
              </li>
            ))}

            {options.length === 0 && (
              <li className="px-3 py-2 text-sm text-[var(--text-ter)] italic">
                No options available
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
